import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Nav } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../../../config';
import MasterPartNoInput from '../../_components/MasterPartNoInput';
import { isPartNoCreateEnabled, MASTER_PART_NO_VALIDATION_STATUS } from '../../utils/masterPartNoValidation';
import { createTpmDefaults } from './tpmDefaults';
import TpmTierPanel from './TpmTierPanel';
import TpmTipsModal from './TpmTipsModal';
import './TpmChecklistForm.css';

const TPM_TIER_TABS = [
    { key: 'tierA', title: 'Tier A' },
    { key: 'tierB', title: 'Tier B' },
    { key: 'tierC', title: 'Tier C' }
];

function getStoredUser() {
    try {
        return JSON.parse(localStorage.getItem('user'));
    } catch (_error) {
        return null;
    }
}

function getSessionHeaders(user) {
    return {
        'Content-Type': 'application/json',
        Authorization: `Basic ${user.authdata || ''}`
    };
}

function parseJson(value, fallback) {
    if (!value || typeof value === 'object') return value || fallback;
    try {
        return JSON.parse(value);
    } catch (_error) {
        return fallback;
    }
}

function normalizeTier(value, fallback) {
    const tier = parseJson(value, null);
    if (!tier || typeof tier !== 'object') return fallback;
    return {
        intervalPcs: tier.intervalPcs === undefined || tier.intervalPcs === null ? fallback.intervalPcs : Number(tier.intervalPcs),
        tasks: Array.isArray(tier.tasks) ? tier.tasks.map((task, index) => ({
            seq: task.seq || index + 1,
            description: task.description || '',
            photoUrls: Array.isArray(task.photoUrls) ? task.photoUrls : []
        })) : fallback.tasks
    };
}

function getToolingRows(body) {
    if (Array.isArray(body)) return body;
    return body && Array.isArray(body.data) ? body.data : [];
}

function getSingleChecklist(body) {
    if (Array.isArray(body)) return body[0] || null;
    if (body && Array.isArray(body.data)) return body.data[0] || null;
    if (body && body.data && typeof body.data === 'object') return body.data;
    return body && body.checklist ? body.checklist : body;
}

function getErrorFromResponse(response) {
    return response.text().then(text => {
        let body = {};
        try {
            body = text ? JSON.parse(text) : {};
        } catch (_error) {
            body = {};
        }
        if (!response.ok) throw new Error(body.error || body.message || 'Request failed.');
        return body;
    });
}

export default function TpmChecklistForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isNew = !id || id === 'new';
    const [partNo, setPartNo] = useState('');
    const [toolingNo, setToolingNo] = useState('');
    const [tiers, setTiers] = useState(createTpmDefaults);
    const [toolingOptions, setToolingOptions] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [partNoValidationStatus, setPartNoValidationStatus] = useState(MASTER_PART_NO_VALIDATION_STATUS.IDLE);
    const [showTips, setShowTips] = useState(false);
    const [activeTier, setActiveTier] = useState('tierA');
    const loadRequestSequence = useRef(0);

    const user = useMemo(getStoredUser, []);

    const loadTooling = useCallback(() => {
        if (!user || !user.accountId) return Promise.reject(new Error('Please sign in to manage TPM checklists.'));
        return fetch(`${config.apiUrl}/getToolingByCompany?company=${encodeURIComponent(user.accountId)}&keyword=&limit=10000&offset=0`, {
            method: 'POST',
            headers: getSessionHeaders(user)
        }).then(getErrorFromResponse).then(getToolingRows);
    }, [user]);

    const loadChecklist = useCallback(() => {
        if (isNew) return Promise.resolve(null);
        if (!user || !user.accountId) return Promise.reject(new Error('Please sign in to manage TPM checklists.'));
        return fetch(`${config.apiUrl}/getOneTpmChecklist?id=${encodeURIComponent(id)}`, {
            method: 'GET',
            headers: getSessionHeaders(user)
        })
            .then(getErrorFromResponse)
            .then(getSingleChecklist)
            .then(checklist => {
                if (!checklist) throw new Error('TPM checklist not found.');
                return checklist;
            });
    }, [id, isNew, user]);

    useEffect(() => {
        const requestId = ++loadRequestSequence.current;
        setLoading(!isNew);
        setError('');
        Promise.all([loadTooling(), loadChecklist()])
            .then(([tooling, checklist]) => {
                if (requestId !== loadRequestSequence.current) return;
                setToolingOptions(tooling);
                if (checklist) {
                    const defaults = createTpmDefaults();
                    setPartNo(checklist.partNo || '');
                    setToolingNo(checklist.toolingNo || '');
                    setTiers({
                        tierA: normalizeTier(checklist.tierA, defaults.tierA),
                        tierB: normalizeTier(checklist.tierB, defaults.tierB),
                        tierC: normalizeTier(checklist.tierC, defaults.tierC)
                    });
                }
                setLoading(false);
            })
            .catch(loadError => {
                if (requestId !== loadRequestSequence.current) return;
                setError(loadError.message || 'Failed to load TPM checklist.');
                setLoading(false);
            });
        return () => {
            requestId === loadRequestSequence.current && (loadRequestSequence.current += 1);
        };
    }, [isNew, loadChecklist, loadTooling]);

    const updateTier = (tierKey, tier) => setTiers(current => Object.assign({}, current, { [tierKey]: tier }));

    const handleToolingChange = event => {
        const nextToolingNo = event.target.value;
        setToolingNo(nextToolingNo);
        if (!partNo.trim()) {
            const matchedTooling = toolingOptions.find(option => String(option.toolingNo) === nextToolingNo);
            if (matchedTooling && matchedTooling.partNo) setPartNo(matchedTooling.partNo);
        }
    };

    const handleSubmit = event => {
        event.preventDefault();
        if (!user || !user.accountId) {
            setError('Please sign in to manage TPM checklists.');
            return;
        }
        if (!isPartNoCreateEnabled(partNoValidationStatus)) return;

        setSaving(true);
        setError('');
        const payload = {
            company: user.accountId,
            partNo,
            toolingNo,
            tierA: tiers.tierA,
            tierB: tiers.tierB,
            tierC: tiers.tierC,
            ...(!isNew ? { id } : {})
        };
        fetch(`${config.apiUrl}/${isNew ? 'createTpmChecklist' : 'updateTpmChecklist'}`, {
            method: 'POST',
            headers: getSessionHeaders(user),
            body: JSON.stringify(payload)
        })
            .then(getErrorFromResponse)
            .then(() => navigate('/tooling/tpm'))
            .catch(saveError => {
                setError(saveError.message || 'Failed to save TPM checklist.');
                setSaving(false);
            });
    };

    const submitDisabled = saving || loading || !isPartNoCreateEnabled(partNoValidationStatus);
    const activeTierTab = TPM_TIER_TABS.find(tab => tab.key === activeTier) || TPM_TIER_TABS[0];

    return (
        <div className="container py-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h1 className="h3 mb-0">{isNew ? 'New TPM Checklist' : 'Edit TPM Checklist'}</h1>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" type="button" onClick={() => setShowTips(true)}>Tier Tips</Button>
                    <Button variant="outline-secondary" type="button" onClick={() => navigate('/tooling/tpm')}>Back</Button>
                </div>
            </div>
            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            {loading ? <div aria-live="polite">Loading TPM checklist...</div> : (
                <Form onSubmit={handleSubmit}>
                    <div className="tpm-checklist-sticky-header" data-testid="tpm-checklist-sticky-header">
                        <div className="row mb-3">
                            <Form.Group className="col-md-6" controlId="tpm-part-no">
                                <Form.Label>Part No</Form.Label>
                                <MasterPartNoInput
                                    value={partNo}
                                    onChange={setPartNo}
                                    enabled
                                    company={user && user.accountId}
                                    onValidationChange={setPartNoValidationStatus}
                                />
                            </Form.Group>
                            <Form.Group className="col-md-6" controlId="tpm-tooling-no">
                                <Form.Label>Tooling No</Form.Label>
                                <Form.Select value={toolingNo} onChange={handleToolingChange}>
                                    <option value="">Select tooling number</option>
                                    {toolingOptions.map(option => (
                                        <option key={option.id || option.toolingNo} value={option.toolingNo}>{option.toolingNo}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <Nav
                            variant="tabs"
                            activeKey={activeTier}
                            onSelect={eventKey => eventKey && setActiveTier(eventKey)}
                            className="tpm-checklist-tabs"
                            role="tablist"
                            aria-label="TPM checklist tiers"
                        >
                            {TPM_TIER_TABS.map(tab => (
                                <Nav.Item key={tab.key}>
                                    <Nav.Link
                                        as="button"
                                        type="button"
                                        eventKey={tab.key}
                                        id={`tpm-tab-${tab.key}`}
                                        role="tab"
                                        aria-selected={activeTier === tab.key}
                                        aria-controls={`tpm-panel-${tab.key}`}
                                    >
                                        {tab.title}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </div>
                    <div
                        id={`tpm-panel-${activeTierTab.key}`}
                        role="tabpanel"
                        aria-labelledby={`tpm-tab-${activeTierTab.key}`}
                    >
                        <TpmTierPanel
                            tierKey={activeTierTab.key}
                            title={activeTierTab.title}
                            tier={tiers[activeTierTab.key]}
                            onChange={updateTier}
                            disabled={saving}
                        />
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" type="button" onClick={() => navigate('/tooling/tpm')}>Cancel</Button>
                        <Button type="submit" disabled={submitDisabled}>{saving ? 'Saving...' : (isNew ? 'Create Checklist' : 'Save Checklist')}</Button>
                    </div>
                </Form>
            )}
            <TpmTipsModal show={showTips} onHide={() => setShowTips(false)} />
        </div>
    );
}
